import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { isPlatformAdminHostname } from '../config/platformAdminHost'
import { postLoginHomePathSafe } from '../utils/coLanding'
import MfaTotpCodeHint from '../components/MfaTotpCodeHint'

const Login = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, completeMfaLogin } = useAuth()
  const sessionExpired = searchParams.get('expired') === '1'
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [buttonHover, setButtonHover] = useState(false)
  const [mfaStep, setMfaStep] = useState(null)
  const [mfaCode, setMfaCode] = useState('')
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const finishLogin = async (result) => {
    const who = result.user?.name || result.user?.username || formData.username
    toast.success(`Welcome back, ${who}!`)
    navigate(
      isPlatformAdminHostname()
        ? '/admin/platform-config'
        : postLoginHomePathSafe(result.user, result.user?.roles || result.roles, result.featureFlags),
      { replace: true }
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const result = await login({ username: formData.username, password: formData.password })
      if (result?.success && result.mfaRequired) {
        setMfaStep({ mfaToken: result.mfaToken, username: result.user?.username || formData.username })
        setMfaCode('')
        toast.success('Enter the 6-digit code from your authenticator app')
      } else if (result?.success && result.user?.token) {
        await finishLogin(result)
      } else {
        toast.error(result?.error || 'Invalid username or password')
      }
    } catch (_) {
      toast.error('Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async (e) => {
    e.preventDefault()
    if (!mfaStep?.mfaToken) return
    setLoading(true)
    try {
      const result = await completeMfaLogin({ mfaToken: mfaStep.mfaToken, code: mfaCode })
      if (result?.success && result.user?.token) {
        await finishLogin(result)
      } else {
        const err = result?.error || 'Invalid verification code'
        toast.error(err)
        if (/session has expired|session is invalid/i.test(err)) {
          setMfaStep(null)
          setMfaCode('')
        }
      }
    } catch (_) {
      toast.error('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const backToPassword = () => {
    setMfaStep(null)
    setMfaCode('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#0A5169'}}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={new URL('../assets/images/paysafego.png', import.meta.url).href} alt="PaySafeGO" className="mx-auto h-[3.75rem]" />
          <p className="mt-2 text-[10px] text-white/50 font-mono">
            v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}
          </p>
        </div>

        {!mfaStep ? (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg space-y-6">
            {sessionExpired && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                Your session has expired. Please log in again.
              </div>
            )}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username or email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:border-transparent focus:z-10 sm:text-sm"
                style={{'--tw-ring-color': '#0A5169'}}
                placeholder="Enter your username or email"
                value={formData.username}
                onChange={handleChange}
                autoComplete="username"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm font-medium" style={{ color: '#0A5169' }}>
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:border-transparent focus:z-10 sm:text-sm"
                  style={{'--tw-ring-color': '#0A5169'}}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                style={{backgroundColor: buttonHover ? '#5F7F8B' : '#0A5169'}}
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </div>
        </form>
        ) : (
        <form className="mt-8 space-y-6" onSubmit={handleMfaSubmit}>
          <div className="bg-white py-8 px-6 shadow-xl rounded-lg space-y-6">
            <div className="text-center">
              <DevicePhoneMobileIcon className="mx-auto h-10 w-10 text-gray-400" />
              <h2 className="mt-2 text-lg font-medium text-gray-900">Two-factor authentication</h2>
              <p className="mt-1 text-sm text-gray-600">
                Signed in as <span className="font-medium">{mfaStep.username}</span>. Enter the 6-digit code from Google Authenticator or Microsoft Authenticator.
              </p>
            </div>
            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                Verification code
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm tracking-widest text-center text-lg"
                style={{'--tw-ring-color': '#0A5169'}}
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
              <MfaTotpCodeHint className="mt-3" />
            </div>
            <div className="space-y-2">
              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50"
                style={{ backgroundColor: '#0A5169' }}
              >
                {loading ? 'Verifying…' : 'Verify and sign in'}
              </button>
              <button
                type="button"
                onClick={backToPassword}
                className="w-full text-sm text-gray-600 hover:text-gray-900"
              >
                Back to password
              </button>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}

export default Login
